import { TrendingDown, X } from 'lucide-react';
import { cn } from '../../lib/cn';
export { downloadCsv, type CsvCell } from '../../lib/csv';
import { formatMoney, formatMoneyCompact, formatPercent } from '../../lib/format';
import {
  StatusPill,
  UTILIZATION_BANDS,
  UTILIZATION_BAND_HEX,
  UTILIZATION_BAND_LABEL,
  UTILIZATION_BAND_TEXT_HEX,
  UTILIZATION_OVER_STRIPE,
  utilizationBand,
  utilizationFillClass,
  utilizationTextClass,
  type UtilizationBand,
} from '../../components/ui';
import type { CostModel, Person, PersonType } from './api';

/** Utilization target ratio (Big Board convention). */
export const UTILIZATION_TARGET = UTILIZATION_BANDS.target;

/** Chart hue — the app's accent blue (single-hue magnitude encoding). */
export const CHART_BLUE = '#2563eb';
export const CHART_BLUE_SOFT = '#bfdbfe';

/** Grey used for "tracked but not billable" everywhere on this feature. */
export const NON_BILLABLE_HEX = '#d4d4d4';
/** No weekly capacity set — unmeasured, not "bad". */
export const NO_CAPACITY_HEX = '#c9d1d9';

/** Bar fill for a utilization ratio — same ramp as the ProgressBar meters. */
export function utilizationHex(ratio: number | null): string {
  return ratio == null ? NO_CAPACITY_HEX : UTILIZATION_BAND_HEX[utilizationBand(ratio)];
}

/** Text/number colour for a ratio — same hue as the bar, contrast-safe. */
export function utilizationTextHex(ratio: number | null): string {
  return ratio == null ? '#9898A5' : UTILIZATION_BAND_TEXT_HEX[utilizationBand(ratio)];
}

export function utilizationLabel(ratio: number | null): string {
  return ratio == null ? 'No capacity set' : UTILIZATION_BAND_LABEL[utilizationBand(ratio)];
}

/**
 * SVG pattern id for the over-capacity hatch. Recharts bars use it instead of a
 * flat fill so the chart carries the same double encoding (red + stripe) as the
 * meters — the person page must not be the one place where over-capacity looks
 * like any other bar.
 */
export const UTIL_OVER_PATTERN_ID = 'util-over-hatch';

/** Recharts fill for a ratio: the hatch pattern for over-capacity, else the hue. */
export function utilizationChartFill(ratio: number | null): string {
  if (ratio != null && utilizationBand(ratio) === 'over') return `url(#${UTIL_OVER_PATTERN_ID})`;
  return utilizationHex(ratio);
}

/**
 * Pattern definition backing {@link utilizationChartFill}.
 *
 * It lives in its own zero-size <svg> rather than inside the chart: Recharts
 * drops children it does not recognise, so a <defs> nested in <BarChart> never
 * reaches the DOM and every over-capacity bar renders as an unfilled gap. SVG
 * paint-server ids are document-scoped, so this resolves fine from the chart.
 */
export function UtilizationChartDefs() {
  return (
    <svg width={0} height={0} className="absolute" aria-hidden focusable="false">
      <defs>
        <pattern
          id={UTIL_OVER_PATTERN_ID}
          width={7}
          height={7}
          patternTransform="rotate(45)"
          patternUnits="userSpaceOnUse"
        >
          <rect width={7} height={7} fill={UTILIZATION_BAND_HEX.over} />
          <line x1={0} y1={0} x2={0} y2={7} stroke="rgba(255,255,255,0.5)" strokeWidth={3} />
        </pattern>
      </defs>
    </svg>
  );
}

const BAND_RANGE: Record<UtilizationBand, string> = {
  idle: '< 60%',
  low: '60–75%',
  target: '75–110%',
  over: '≥ 110%',
};

const LEGEND_BANDS: UtilizationBand[] = ['idle', 'low', 'target', 'over'];

/** Swatch that matches the bar exactly, hatch included. */
function BandSwatch({ band, className }: { band: UtilizationBand; className?: string }) {
  return (
    <span
      className={cn(
        'h-2.5 w-2.5 shrink-0 rounded-sm',
        band === 'over' && UTILIZATION_OVER_STRIPE,
        className,
      )}
      style={{ backgroundColor: UTILIZATION_BAND_HEX[band] }}
      aria-hidden
    />
  );
}

/** Colour key for the utilization ramp — the chart is meaningless without it. */
export function UtilizationLegend({ className }: { className?: string }) {
  return (
    <ul className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5', className)}>
      {LEGEND_BANDS.map((band) => (
        <li key={band} className="inline-flex items-center gap-1.5 text-caption text-grey-500">
          <BandSwatch band={band} />
          <span className="text-secondary-700">{UTILIZATION_BAND_LABEL[band]}</span>
          <span className="num">{BAND_RANGE[band]}</span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Triage chips — the legend nobody reads, turned into the counts a manager
// actually wants ("3 under-booked · 1 over capacity") and a filter on the table.
// ---------------------------------------------------------------------------

/** Band filter, plus `unmeasured` for people with no weekly capacity. */
export type BandFilter = UtilizationBand | 'unmeasured';

/**
 * What the chip row can filter the table down to. `negative` is not a
 * utilization band at all — it is the money question ("who lost money this
 * month"), and it is deliberately first so the loudest control on the page
 * points at the loss, not at the busiest person.
 */
export type RowFilter = BandFilter | 'negative';

export type BandCounts = Record<UtilizationBand, number> & { unmeasured: number };

const CHIP_LABEL: Record<RowFilter, string> = {
  negative: 'losing money',
  idle: 'under-booked',
  low: 'below target',
  target: 'on target',
  over: 'over capacity',
  unmeasured: 'no capacity',
};

/** Counts per band across a set of people (the triage summary). */
export function countBands(
  people: Array<{ currentMonth: { utilization: number | null } }>,
): BandCounts {
  const counts: BandCounts = { idle: 0, low: 0, target: 0, over: 0, unmeasured: 0 };
  for (const p of people) {
    const u = p.currentMonth.utilization;
    if (u == null) counts.unmeasured += 1;
    else counts[utilizationBand(u)] += 1;
  }
  return counts;
}

export function bandOf(utilization: number | null): BandFilter {
  return utilization == null ? 'unmeasured' : utilizationBand(utilization);
}

export interface TeamMoney {
  revenue: number;
  cost: number;
  margin: number;
  /** People whose month-to-date margin is below zero. */
  negativeCount: number;
  /** Σ of those negative margins (≤ 0). */
  negativeMargin: number;
  /**
   * Cost carried by capacity nobody bought: Σ cost × (1 − utilization) across
   * people under the utilization target. This is what "under-booked" costs, and
   * it is the number that makes a quiet 24% salaried row louder than a red 115%
   * contractor row.
   */
  idleCost: number;
}

type MoneyMonth = {
  currentMonth: { utilization: number | null; revenue: number; cost: number; margin: number };
};

/** One definition of the page's money, shared by the KPI card and the chips. */
export function summarizeMoney(people: MoneyMonth[]): TeamMoney {
  const money: TeamMoney = {
    revenue: 0,
    cost: 0,
    margin: 0,
    negativeCount: 0,
    negativeMargin: 0,
    idleCost: 0,
  };
  for (const p of people) {
    const m = p.currentMonth;
    money.revenue += m.revenue;
    money.cost += m.cost;
    money.margin += m.margin;
    if (m.margin < 0) {
      money.negativeCount += 1;
      money.negativeMargin += m.margin;
    }
    if (m.utilization != null && m.utilization < UTILIZATION_BANDS.target) {
      money.idleCost += m.cost * (1 - Math.max(0, m.utilization));
    }
  }
  const round = (n: number) => Math.round(n * 100) / 100;
  return {
    revenue: round(money.revenue),
    cost: round(money.cost),
    margin: round(money.margin),
    negativeCount: money.negativeCount,
    negativeMargin: round(money.negativeMargin),
    idleCost: round(money.idleCost),
  };
}

/** Compact money for chips/KPI hints: $11.2K. */
export function compactMoney(amount: number): string {
  return formatMoneyCompact(Math.abs(amount));
}

// ---------------------------------------------------------------------------
// Sparkline scale
// ---------------------------------------------------------------------------

export interface SparkWindow {
  lo: number;
  hi: number;
  span: number;
}

/**
 * A plotting window padded around the data and snapped to `step`.
 *
 * A 0-anchored 12-bar spark of a series that lives between 56% and 74% is
 * twelve identical blocks — the encoding carries no information and no two
 * months can be ranked. Callers MUST print `lo`–`hi` next to the chart: a
 * truncated baseline is legitimate only when it is stated.
 */
export function paddedWindow(
  values: number[],
  { step, pad = 0, include }: { step: number; pad?: number; include?: number },
): SparkWindow {
  if (values.length === 0) return { lo: 0, hi: step, span: step };
  let lo = Math.max(0, Math.floor((Math.min(...values) - pad) / step) * step);
  let hi = Math.ceil((Math.max(...values) + pad) / step) * step;
  if (include != null) {
    lo = Math.min(lo, include);
    hi = Math.max(hi, include);
  }
  return { lo, hi, span: Math.max(hi - lo, step) };
}

export interface MonthlySparkProps<T extends { month: string }> {
  months: T[];
  /** null = no data for that month (drawn as a faint stub). */
  value: (m: T) => number | null;
  /** Bar colour for a value. */
  color: (value: number | null) => string;
  window: SparkWindow;
  /** Dashed reference drawn across the plot when it falls inside the window. */
  reference?: number;
  /** The month the page is showing — marked by a rail, not a heavy ring. */
  selectedMonth?: string;
  tooltip: (m: T, value: number | null) => string;
  ariaLabel: string;
  className?: string;
}

/**
 * Twelve monthly bars on a stated window, with a "you are here" rail beneath.
 * The rail replaced a heavy grey ring that was visually louder than the bar it
 * was marking.
 */
export function MonthlySpark<T extends { month: string }>({
  months,
  value,
  color,
  window: win,
  reference,
  selectedMonth,
  tooltip,
  ariaLabel,
  className,
}: MonthlySparkProps<T>) {
  const heightPct = (v: number) => ((v - win.lo) / win.span) * 100;
  return (
    <div className={className}>
      <div className="relative flex h-12 items-end gap-[3px]" role="img" aria-label={ariaLabel}>
        {reference != null && reference >= win.lo && reference <= win.hi && (
          <span
            className="pointer-events-none absolute inset-x-0 border-t border-dashed border-secondary-900/35"
            style={{ bottom: `${heightPct(reference)}%` }}
            aria-hidden
          />
        )}
        {months.map((m) => {
          const v = value(m);
          return (
            <span
              key={m.month}
              className="relative flex-1 rounded-sm"
              title={tooltip(m, v)}
              style={{
                height: v == null ? '3px' : `${Math.max(3, Math.min(100, heightPct(v)))}%`,
                backgroundColor: color(v),
                opacity: v == null ? 0.4 : 1,
              }}
            />
          );
        })}
      </div>
      <div className="mt-1 flex gap-[3px]" aria-hidden>
        {months.map((m) => (
          <span
            key={m.month}
            className={cn(
              'h-[2px] flex-1 rounded-full',
              m.month === selectedMonth ? 'bg-secondary-900' : 'bg-grey-200',
            )}
          />
        ))}
      </div>
    </div>
  );
}

export function UtilizationTriageChips({
  counts,
  money,
  value,
  onChange,
  className,
}: {
  counts: BandCounts;
  money: TeamMoney;
  value: RowFilter | null;
  onChange: (next: RowFilter | null) => void;
  className?: string;
}) {
  const chips: RowFilter[] = ['negative', ...LEGEND_BANDS];
  if (counts.unmeasured > 0) chips.push('unmeasured');

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)} role="group" aria-label="Filter by margin or utilization band">
      {chips.map((chip) => {
        const count = chip === 'negative' ? money.negativeCount : counts[chip];
        const active = value === chip;
        const empty = count === 0;
        // Money rides on the two chips where it changes the verdict: what the
        // loss actually is, and what the idle capacity actually costs.
        const suffix =
          chip === 'negative' && money.negativeMargin < 0
            ? `(${compactMoney(money.negativeMargin)})`
            : chip === 'idle' && money.idleCost >= 500
              ? `${compactMoney(money.idleCost)} idle`
              : null;
        return (
          <button
            key={chip}
            type="button"
            aria-pressed={active}
            disabled={empty && !active}
            onClick={() => onChange(active ? null : chip)}
            title={
              chip === 'negative'
                ? 'Margin below zero this month — revenue at bill rates minus cost'
                : chip === 'unmeasured'
                  ? 'No weekly capacity set — excluded from utilization'
                  : chip === 'idle'
                    ? `Under-booked · ${BAND_RANGE.idle} of capacity — the cost of the capacity nobody bought`
                    : `${UTILIZATION_BAND_LABEL[chip as UtilizationBand]} · ${BAND_RANGE[chip as UtilizationBand]} of capacity`
            }
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-caption transition-colors',
              active
                ? 'border-secondary-900 bg-secondary-900 text-white shadow-sm'
                : chip === 'negative' && !empty
                  ? 'border-status-late-border bg-status-late-bg text-status-late hover:border-status-late'
                  : 'border-grey-200 bg-white text-secondary-700 hover:border-grey-300 hover:bg-grey-50',
              empty && !active && 'opacity-40',
            )}
          >
            {chip === 'negative' ? (
              <TrendingDown className="h-3 w-3 shrink-0" aria-hidden />
            ) : chip === 'unmeasured' ? (
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-sm border border-grey-300"
                style={{ backgroundColor: NO_CAPACITY_HEX }}
                aria-hidden
              />
            ) : (
              <BandSwatch band={chip as UtilizationBand} />
            )}
            <span className="num font-bold">{count}</span>
            <span className="font-medium">{CHIP_LABEL[chip]}</span>
            {suffix && (
              <span
                className={cn(
                  'num font-semibold',
                  active ? 'text-white/80' : chip === 'negative' ? '' : 'text-grey-500',
                )}
              >
                · {suffix}
              </span>
            )}
          </button>
        );
      })}
      {value != null && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-caption font-semibold text-grey-500 transition-colors hover:text-secondary"
        >
          <X className="h-3 w-3" aria-hidden /> Clear
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capacity bar — billable / non-billable / unfilled, against capacity
// ---------------------------------------------------------------------------

/** The row bar runs to 125% of capacity so overflow past the tick is visible. */
const CAPACITY_SCALE = 1.25;

export interface CapacityBarProps {
  /** For the accessible label. */
  name: string;
  billableHours: number;
  trackedHours: number;
  capacityHours: number;
  /** billable ÷ capacity as returned by the API (null = no capacity set). */
  utilization: number | null;
  /** Width classes for the track (the value sits to its right). */
  barClassName?: string;
  className?: string;
}

/**
 * Three-segment meter against capacity: billable (on the utilization ramp),
 * non-billable (grey), unfilled remainder (empty track), with a tick at 100%
 * capacity and any overflow drawn past it.
 *
 * A single-value meter cannot tell "fully occupied, badly sold" (56 h tracked,
 * 17 h billable) from "partly absent AND badly sold" (45 h tracked, 13 h
 * billable) — both render as the same short stub. This can: the first bar is
 * full of grey, the second stops short of the tick.
 */
export function CapacityBar({
  name,
  billableHours,
  trackedHours,
  capacityHours,
  utilization,
  barClassName,
  className,
}: CapacityBarProps) {
  const measured = capacityHours > 0 && utilization != null;
  const band = utilization != null ? utilizationBand(utilization) : null;
  const nonBillable = Math.max(0, trackedHours - billableHours);

  // Cumulative so the two segments can never sum past the track.
  const toPct = (hours: number) =>
    capacityHours > 0
      ? (Math.min(Math.max(hours, 0) / capacityHours, CAPACITY_SCALE) / CAPACITY_SCALE) * 100
      : 0;
  const billablePct = toPct(billableHours);
  const trackedPct = toPct(billableHours + nonBillable);
  const nonBillablePct = Math.max(0, trackedPct - billablePct);
  const tickPct = (1 / CAPACITY_SCALE) * 100;

  const label = measured
    ? `${name}: ${formatPercent(utilization!, { decimals: 0 })} of capacity — ${utilizationLabel(
        utilization,
      )}. ${formatHoursFixed(billableHours)} h billable, ${formatHoursFixed(
        nonBillable,
      )} h non-billable, of ${formatHoursFixed(capacityHours)} h capacity.`
    : `${name}: no weekly capacity set — ${formatHoursFixed(trackedHours)} h tracked`;

  return (
    <span className={cn('inline-flex items-center justify-end gap-2', className)}>
      <span className={cn('relative block shrink-0 py-[3px]', barClassName)} role="img" aria-label={label}>
        <span
          className={cn(
            'relative block h-2.5 w-full overflow-hidden rounded-full',
            measured ? 'bg-grey-100' : 'bg-grey-100 opacity-70',
          )}
        >
          {measured ? (
            <>
              {billablePct > 0 && (
                <span
                  className={cn('absolute inset-y-0 left-0', utilizationFillClass[band!])}
                  style={{ width: `${billablePct}%` }}
                />
              )}
              {nonBillablePct > 0.2 && (
                <span
                  className="absolute inset-y-0 bg-grey-300"
                  style={{ left: `${billablePct}%`, width: `${nonBillablePct}%` }}
                />
              )}
            </>
          ) : (
            <span className="absolute inset-0 bg-[repeating-linear-gradient(135deg,#eaeaea_0_3px,transparent_3px_7px)]" />
          )}
        </span>
        {measured && (
          <span
            className="absolute inset-y-0 w-[2px] rounded-full bg-secondary-900/80"
            style={{ left: `${tickPct}%` }}
            aria-hidden
          />
        )}
      </span>
      <span
        className={cn(
          'num w-11 shrink-0 text-right text-body-sm font-bold',
          measured ? utilizationTextClass[band!] : 'text-grey-400',
        )}
      >
        {measured ? formatPercent(utilization!, { decimals: 0 }) : '—'}
      </span>
    </span>
  );
}

/** Miniature worked example so the three segments need no prose to decode. */
export function CapacityBarKey({ className }: { className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2 text-caption text-grey-500', className)}>
      <span className="relative block w-16 shrink-0 py-[3px]" aria-hidden>
        <span className="relative block h-2.5 w-full overflow-hidden rounded-full bg-grey-100">
          <span className="absolute inset-y-0 left-0 w-[48%] bg-success" />
          <span className="absolute inset-y-0 left-[48%] w-[26%] bg-grey-300" />
        </span>
        <span className="absolute inset-y-0 left-[80%] w-[2px] rounded-full bg-secondary-900/80" />
      </span>
      <span>
        billable · <span className="text-grey-600">non-billable</span> · unfilled
        <span className="hidden sm:inline"> — tick at 100% capacity</span>
      </span>
    </span>
  );
}

/**
 * Hours in right-aligned numeric columns: always 2dp so decimal points line up
 * on a vertical scan (money already does this).
 */
export function formatHoursFixed(hours: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(hours);
}

/** "In progress" marker for a month that has not finished yet. */
export function InProgressPill({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-md border border-status-partial-border bg-status-partial-bg px-1.5 py-0.5 text-[0.6875rem] font-bold uppercase tracking-wide text-status-partial',
        className,
      )}
      title="Month to date — capacity and cost are prorated to business days elapsed"
    >
      In progress
    </span>
  );
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

const avatarSizes = { sm: 'avatar-sm', md: 'avatar-md', lg: 'avatar-lg' } as const;

export function Avatar({ name, size = 'sm' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span className={cn('avatar shrink-0', avatarSizes[size])} aria-hidden>
      {initials(name)}
    </span>
  );
}

export function TypeBadge({ type }: { type: PersonType }) {
  return (
    <StatusPill
      status={type === 'STAFF' ? 'open' : 'partial'}
      label={type === 'STAFF' ? 'Staff' : 'Contractor'}
      dot={false}
    />
  );
}

/** Cost-basis chip: "$70/hr cost" or "$10,400/mo". */
export function CostModelChip({
  costModel,
  hourlyCost,
  flatMonthlyCost,
  className,
}: Pick<Person, 'costModel' | 'hourlyCost' | 'flatMonthlyCost'> & { className?: string }) {
  const label =
    costModel === 'HOURLY'
      ? `${formatMoney(hourlyCost ?? 0, { whole: true })}/hr`
      : `${formatMoney(flatMonthlyCost ?? 0, { whole: true })}/mo`;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-grey-200 bg-grey-50 px-2 py-0.5 text-xs font-semibold text-secondary-700 num',
        className,
      )}
      title={costModel === 'HOURLY' ? 'Hourly cost' : 'Flat monthly cost'}
    >
      {label}
      <span className="font-medium text-grey-400">{costModel === 'HOURLY' ? 'cost' : 'flat'}</span>
    </span>
  );
}

export function costModelLabel(costModel: CostModel): string {
  return costModel === 'HOURLY' ? 'Hourly cost' : 'Flat monthly';
}
